import type { PanelConfig, PanelConfigInput, PanelWidth } from '@/types/panels';
import { PanelMode, PanelTheme } from '@/types/panels';

import { DEFAULT_PANEL_ORDER, DEFAULT_WIDTH } from './allocate';

const VALID_THEMES: readonly string[] = Object.values(PanelTheme);
const VALID_MODES: readonly string[] = Object.values(PanelMode);

/** Panel descriptor. */
export function definePanel(config: PanelConfigInput): PanelConfig {
    if (import.meta.env.DEV) { validate(config); }

    const modes = config.modes ?? [PanelMode.View, PanelMode.Settings];
    return {
        id: config.id,
        title: config.title,
        description: config.description ?? '',
        icon: config.icon ?? null,
        launcherTabs: config.launcherTabs ?? [],
        width: { ...DEFAULT_WIDTH, ...(config.width ?? {}) },
        getWidth: config.getWidth,
        modes,
        themes: config.themes ?? PanelTheme.Any,
        excludes: [],
        autoOpenIds: config.autoOpenIds ?? [],
        autoChild: config.autoChild ?? null,
        summoner: config.summoner ?? null,
        previewTabs: config.previewTabs ?? [],
        shouldKeepMounted: config.shouldKeepMounted ?? true,
        isOffStage: config.isOffStage ?? false,
        isOverlay: config.isOverlay ?? false,
        order: config.order ?? DEFAULT_PANEL_ORDER,
        navOrder: config.navOrder ?? config.order ?? DEFAULT_PANEL_ORDER,
        navGroup: config.navGroup ??
            (modes.includes(PanelMode.View) ? PanelMode.View : PanelMode.Settings),
        hasNavButton: config.hasNavButton ?? true,
        requiresAdmin: config.requiresAdmin ?? false,
        isHeaderless: config.isHeaderless ?? false,
        isTransparent: config.isTransparent ?? false,
        hasSelectedSurface: config.hasSelectedSurface ?? false,
        hasVideo: config.hasVideo ?? false,
        shouldClip: config.shouldClip ?? false,
        urlSyncedProps: config.urlSyncedProps ?? [],
        subjectProps: config.subjectProps ?? [],
    };
}

function validate(config: PanelConfigInput): void {
    if (!config.id || typeof config.id !== 'string') {
        throw new Error('panel: id is required');
    }
    if (!config.title) {
        throw new Error(`panel ${config.id}: title is required`);
    }
    if (config.excludes !== undefined) {
        throw new Error(
            `panel ${config.id}: 'excludes' is no longer declared per-panel — ` +
            'add it to src/app/exclusions.ts',
        );
    }
    if (config.autoChild !== undefined && config.autoChild !== null) {
        const isValid = typeof config.autoChild === 'string' ||
            (typeof config.autoChild === 'object' &&
                !Array.isArray(config.autoChild) &&
                Object.keys(config.autoChild).length > 0 &&
                Object.keys(config.autoChild).every((key) => VALID_MODES.includes(key)) &&
                Object.values(config.autoChild).every((value) => typeof value === 'string' && value));
        if (!isValid) {
            throw new Error(
                `panel ${config.id}: autoChild must be a panel id or a { view, settings } map of panel ids`,
            );
        }
    }
    if (config.summoner !== undefined && config.summoner !== null) {
        const { id, props } = config.summoner;
        const isValid = typeof id === 'string' && id && id !== config.id && Array.isArray(props) &&
            props.every((prop) => typeof prop === 'string' && prop);
        if (!isValid) {
            throw new Error(
                `panel ${config.id}: summoner must name another panel and its props`);
        }
    }
    if (config.themes && !VALID_THEMES.includes(config.themes)) {
        throw new Error(`panel ${config.id}: themes must be one of ${VALID_THEMES.join(', ')}`);
    }
    for (const mode of config.modes ?? []) {
        if (!VALID_MODES.includes(mode)) {
            throw new Error(`panel ${config.id}: invalid mode "${mode}"`);
        }
    }

    const width: PanelWidth = { ...DEFAULT_WIDTH, ...(config.width ?? {}) };
    if (!(width.min > 0) || width.min > width.max) {
        throw new Error(`panel ${config.id}: invalid width bounds`);
    }
    if (width.default < width.min || width.default > width.max) {
        throw new Error(`panel ${config.id}: width.default outside [min, max]`);
    }
    for (const flag of ['fill', 'keepsDefaultWidth'] as const) {
        if (typeof width[flag] !== 'boolean') {
            throw new Error(`panel ${config.id}: width.${flag} must be boolean`);
        }
    }
    if (config.getWidth !== undefined && typeof config.getWidth !== 'function') {
        throw new Error(`panel ${config.id}: getWidth must be a function`);
    }
}
